import { useState } from 'react';
import conversationService from '../services/conversationService';
import useNotification from './useNotification';
import {
  fieldToMarkdown,
  markdownToField,
  fieldsToMarkdown,
  formatFieldForCopy
} from '../utils/fieldValidationParser';

/**
 * Custom hook for CRUD operations on field validations
 * Manages edit, save, and delete operations for field validation rules
 */
const useFieldValidationCRUD = (
  conversation,
  fields,
  setFields,
  contentRef,
  onConversationUpdate,
  updateCurrentVersion = null,
  currentVersion = null,
  isLocalUpdateRef = null
) => {
  const [editingField, setEditingField] = useState(null);
  const [editedContent, setEditedContent] = useState('');
  const [editedTitle, setEditedTitle] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ open: false, field: null });

  const { showNotification } = useNotification();

  /**
   * Persist sections to ParsedResponse — backend syncs markdown to conversation message.
   */
  const _persistToBackend = async (updatedFields) => {
    let messageTimestamp = currentVersion?.timestamp;
    // Fallback: find timestamp from last assistant message in conversation
    if (!messageTimestamp && conversation?.messages?.length) {
      for (let i = conversation.messages.length - 1; i >= 0; i--) {
        if (conversation.messages[i].role === 'assistant') {
          messageTimestamp = conversation.messages[i].createdAt;
          break;
        }
      }
    }
    if (!messageTimestamp || !conversation?._id) {
      throw new Error('Missing timestamp or conversation ID for save');
    }

    // Convert fields to section format for updateParsedSections
    const sections = updatedFields.map((f, i) => ({
      id: f.id || `field-${i}`,
      title: f.title || f.entity || `Field ${i + 1}`,
      content: fieldToMarkdown(f),
      rawContent: fieldToMarkdown(f),
      category: f.category || null,
      isFirstInCategory: f.isFirstInCategory || false,
      isCategory: false,
      level: 2,
    }));
    const result = await conversationService.updateParsedSections(
      conversation._id,
      messageTimestamp,
      sections
    );
    if (result?.data?.markdown) {
      contentRef.current = result.data.markdown;
    }
  };

  // Handler for starting edit mode
  const handleStartEdit = (fieldId, field, title = '') => {
    setEditingField(fieldId);
    // Convert the field object to editable markdown format (without header metadata)
    const editableContent = fieldToMarkdown(field);
    setEditedContent(editableContent);
    // Set the title separately for editing
    setEditedTitle(title || field.label || field.name || '');
  };

  // Handler for canceling edit
  const handleCancelEdit = () => {
    setEditingField(null);
    setEditedContent('');
    setEditedTitle('');
  };

  // Handler for saving edited content
  const handleSaveEdit = async (fieldIdOrIndex) => {
    try {
      setIsSaving(true);

      // Set the flag to prevent version hook from re-processing this as a new version
      if (isLocalUpdateRef) {
        isLocalUpdateRef.current = true;
        console.log('🔒 Set isLocalUpdateRef.current = true (Field Validation)');
      }

      // Parse the edited content back into field structure
      const updatedFields = [...fields];

      // Find the actual index in the fields array
      // fieldIdOrIndex could be either the field ID or the index (for backward compatibility)
      const fieldIndex = typeof fieldIdOrIndex === 'string'
        ? updatedFields.findIndex(f => f.id === fieldIdOrIndex)
        : fieldIdOrIndex;

      if (fieldIndex === -1) {
        throw new Error('Field not found in the fields array');
      }

      const originalField = updatedFields[fieldIndex];

      // Parse markdown back to field object, preserving the original category
      const parsedField = markdownToField(editedContent, originalField.id, originalField.group);

      // Update the field label/name with editedTitle if provided
      if (editedTitle) {
        parsedField.label = editedTitle;
        // Also update name if it was the same as label originally
        if (originalField.name === originalField.label) {
          parsedField.name = editedTitle;
        }
      }

      // Update the field in the array
      updatedFields[fieldIndex] = parsedField;
      setFields(updatedFields);

      // Convert updated fields back to markdown content
      const updatedContent = fieldsToMarkdown(updatedFields);

      // Update the ref
      contentRef.current = updatedContent;

      // Save to backend (ParsedResponse first, conversation message fallback)
      if (conversation && conversation.messages && conversation.messages.length > 0) {
        await _persistToBackend(updatedFields);

        // Update the current version BEFORE calling onConversationUpdate
        // This ensures the version state is updated before any re-renders
        if (updateCurrentVersion && currentVersion) {
          updateCurrentVersion({
            ...currentVersion,
            fields: updatedFields,
            content: updatedContent,
            parsedContent: updatedFields
          });
        }

        // Call the parent's update handler if provided
        // Notify parent component
        if (onConversationUpdate) {
          await onConversationUpdate(conversation._id, conversation.messages);
        }
      }

      // Exit edit mode
      setEditingField(null);
      setEditedContent('');
      setEditedTitle('');

      showNotification({
        message: "Field validation updated successfully",
        type: "success"
      });
    } catch (error) {
      console.error('Error saving field validation:', error);
      showNotification({
        message: "Failed to update field validation",
        type: "error"
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handler for deleting field
  const handleDeleteField = (field) => {
    setDeleteConfirmation({ open: true, field });
  };

  // Handler for confirmed delete
  const handleDeleteFieldConfirmed = async () => {
    const field = deleteConfirmation.field;
    setDeleteConfirmation({ open: false, field: null });

    if (!field) return;

    try {
      // Set the flag to prevent version hook from re-processing this as a new version
      if (isLocalUpdateRef) {
        isLocalUpdateRef.current = true;
        console.log('🔒 Set isLocalUpdateRef.current = true (Field Validation Delete)');
      }

      // Remove the field from the fields array
      const updatedFields = fields.filter(f => f.id !== field.id);
      setFields(updatedFields);

      // Convert updated fields back to markdown content
      const updatedContent = fieldsToMarkdown(updatedFields);

      // Update the ref
      contentRef.current = updatedContent;

      // Save to backend (ParsedResponse first, conversation message fallback)
      if (conversation && conversation.messages && conversation.messages.length > 0) {
        await _persistToBackend(updatedFields);

        // Update the current version BEFORE calling onConversationUpdate
        if (updateCurrentVersion && currentVersion) {
          updateCurrentVersion({
            ...currentVersion,
            fields: updatedFields,
            content: updatedContent,
            parsedContent: updatedFields
          });
        }

        // Call the parent's update handler if provided
        // Notify parent component
        if (onConversationUpdate) {
          await onConversationUpdate(conversation._id, conversation.messages);
        }
      }

      showNotification({
        message: `Field validation "${field.label || field.name}" deleted successfully`,
        type: "success"
      });
    } catch (error) {
      console.error("Error deleting field:", error);
      showNotification({
        message: "Failed to delete field validation",
        type: "error"
      });
    }
  };

  // Handler for copying field to clipboard with HTML table formatting
  const handleCopyField = async (field) => {
    try {
      const { html, text } = formatFieldForCopy(field);

      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' })
        })
      ]);
      showNotification({
        message: `"${field.label || field.name}" copied to clipboard`,
        type: "success"
      });
    } catch (error) {
      showNotification({
        message: `Failed to copy "${field.label || field.name}" to clipboard`,
        type: "error"
      });
    }
  };

  // Handler for adding new field from modal data
  const handleAddField = async (newFieldData) => {
    try {
      // Set the flag to prevent version hook from re-processing this as a new version
      if (isLocalUpdateRef) {
        isLocalUpdateRef.current = true;
        console.log('🔒 Set isLocalUpdateRef.current = true (Field Validation Add)');
      }

      // Create a new field with proper ID
      const newFieldId = `field-${newFieldData.name.toLowerCase().replace(/\s+/g, '-')}`;
      const newField = {
        id: newFieldId,
        name: newFieldData.name,
        label: newFieldData.label || newFieldData.name,
        type: newFieldData.type || 'Text',
        description: newFieldData.description || '',
        group: newFieldData.group || 'General',
        usageContext: newFieldData.usageContext || '', // Add usageContext property
        validations: newFieldData.validations || [
          {
            type: 'required',
            value: true,
            message: 'This field is required'
          }
        ],
        implementation: newFieldData.implementation || ''
      };

      // Add the new field to the array
      const updatedFields = [...fields, newField];
      setFields(updatedFields);

      // Mark first in category for the new field
      const seenCategories = new Set();
      updatedFields.forEach(f => {
        if (f.group && !seenCategories.has(f.group)) {
          f.isFirstInCategory = true;
          seenCategories.add(f.group);
        } else {
          f.isFirstInCategory = false;
        }
      });

      // Convert updated fields back to markdown content
      const updatedContent = fieldsToMarkdown(updatedFields);

      // Update the ref
      contentRef.current = updatedContent;

      // Save to backend (ParsedResponse first, conversation message fallback)
      if (conversation && conversation.messages && conversation.messages.length > 0) {
        await _persistToBackend(updatedFields);

        // Update the current version BEFORE calling onConversationUpdate
        if (updateCurrentVersion && currentVersion) {
          updateCurrentVersion({
            ...currentVersion,
            fields: updatedFields,
            content: updatedContent,
            parsedContent: updatedFields
          });
        }

        // Call the parent's update handler if provided
        // Notify parent component
        if (onConversationUpdate) {
          await onConversationUpdate(conversation._id, conversation.messages);
        }
      }

      showNotification({
        message: `Field validation "${newField.label}" added successfully`,
        type: "success"
      });

      return newField;
    } catch (error) {
      console.error('Error adding field validation:', error);
      showNotification({
        message: "Failed to add field validation",
        type: "error"
      });
      throw error;
    }
  };

  return {
    editingField,
    editedContent,
    editedTitle,
    isSaving,
    deleteConfirmation,
    setEditedContent,
    setEditedTitle,
    setDeleteConfirmation,
    handleStartEdit,
    handleCancelEdit,
    handleSaveEdit,
    handleDeleteField,
    handleDeleteFieldConfirmed,
    handleCopyField,
    handleAddField
  };
};

export default useFieldValidationCRUD;
